import { Button, InputAdornment, TextField, useTheme } from '@material-ui/core'
import React from 'react'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import AccountCircleIcon from '@material-ui/icons/AccountCircle'
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined'

import IconButton from '../../components/IconButton'
import { updateCharacter } from '../../api/character'
import { usePaperExpand } from './util'
import { addEvent } from '../../api/events'
import { CHAR_CONDITION } from '../../constants'

const Handicap = ({ handicap = 0, onChange, theme, threshold }) => {
  const handleChange = (value) => {
    const newValue = handicap + value
    if (newValue < 0 || newValue > threshold) return
    onChange(newValue)
  }
  return (
    <div css={`
      display: flex;
      justify-content: space-between;
      align-items: center;
      background-color: ${theme.palette.background.default};
      border: 1px solid rgba(0, 0, 0, 0.23);
      border-radius: 4px;
      position: relative;
      z-index: 1;
      height: 30px;
      margin-top: 2px;
      padding: 0 4px;

      .btn {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        background-color: grey;
        color: white;
        border-radius: 4px;
        font-weight: bold;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;

        &.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    `}>
      <div className={`btn${handicap === 0 ? ' disabled' : ''}`} onClick={handleChange.bind(this, -1)}>-</div>
      <div css={`font-size: 11px; text-align: center; line-height: 1;`}>
        <div>Handicap</div>
        <div>{handicap}</div>
      </div>
      <div className={`btn${handicap === threshold ? ' disabled' : ''}`} onClick={handleChange.bind(this, 1)}>+</div>
    </div>
  )
}

const typeToHandicap = {
  'Wounds': 'wound handicap',
  'Strain': 'strain handicap'
}

const EditInput = ({ characterName, defaultValue, maxValue: _maxValue, handicap, onSubmit, theme, type }) => {
  const inputRef = React.useRef(null)
  const [ value, setValue ] = React.useState(defaultValue || 0)
  const [ newHandicap, setNewHandicap ] = React.useState(handicap || 0)
  const [ isEditing, setIsEditing ] = React.useState(false)
  const [ isSaving, setIsSaving ] = React.useState(false)

  const maxValue = typeof _maxValue === 'string' && _maxValue.length > 0 ? parseInt(_maxValue) : _maxValue
  const hasMaxValue = maxValue > 0

  const handleSubmit = React.useCallback(async () => {
    if (typeof value === 'number' && value >= 0) {
      if ((value === defaultValue) && (newHandicap === handicap)) return setIsEditing(false)

      let eventText = []
      if (value !== defaultValue) eventText.push(`${characterName}'s ${type.toLowerCase()} changed from ${defaultValue || 0} to ${value}`)
      if (newHandicap !== handicap) eventText.push(`${characterName}'s ${typeToHandicap[type]} changed from ${handicap || 0} to ${newHandicap}`)
      setIsSaving(true)

      try {
        await onSubmit(value, newHandicap, eventText.join('\n'))
      } catch(e) {
        console.error(e)
      }
    }

    setIsSaving(false)
    setIsEditing(false)
  }, [ characterName, defaultValue, handicap, type, newHandicap, onSubmit, value ])

  React.useEffect(() => {
    if (typeof defaultValue === 'number') setValue(defaultValue)
  }, [ defaultValue ])

  React.useEffect(() => {
    setNewHandicap(handicap)
  }, [ handicap ])

  React.useEffect(() => {
    if (!isEditing && value === '') setValue(0)
  }, [ isEditing, value ])

  React.useEffect(() => {
    if (!isEditing) setNewHandicap(handicap)
  }, [ isEditing, handicap ])

  React.useEffect(() => {
    const handler = (e) => {
      const path = e.path || (e.composedPath && e.composedPath())

      if (!path.includes(inputRef.current)) {
        setIsEditing(false)
        setValue(defaultValue)
      }
    }

    const enterHandler = (e) => {
      console.log(e.key)
      if (e.key === 'Enter') {
        handleSubmit()
      }
    }

    if (isEditing) {
      document.getElementById('root').addEventListener('click', handler)
      document.getElementById('root').addEventListener('keypress', enterHandler)
    } else {
      
    }

    return () => {
      document.getElementById('root').removeEventListener('click', handler)
      document.getElementById('root').removeEventListener('keypress', enterHandler)
    }
  }, [ defaultValue, handleSubmit, isEditing ])

  return (
    <div
      css={`
        width: 113px;
        height: 40px;

        /* Chrome, Safari, Edge, Opera */
        input::-webkit-outer-spin-button,
        input::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        /* Firefox */
        input[type=number] {
          -moz-appearance: textfield;
        }

        .MuiOutlinedInput-adornedEnd {
          padding-right: 0px;
        }
      `}
      ref={inputRef}>
      {isEditing ?
        <div>
          <TextField
            disabled={isSaving}
            autoFocus
            variant='outlined'
            value={value}
            size='small'
            placeholder='0'
            type='number'
            onChange={({ target }) => {
              let value = parseInt(target.value, 10)
              if (value > maxValue - handicap) value = maxValue - handicap
              setValue(isNaN(value) ? '' : value)
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end' css={`padding-right: 0px;`}>
                  / {typeof handicap === 'number' ? maxValue - handicap : maxValue}
                  <Button variant='contained' color='primary' title='Save' css={`min-width: unset; margin: 0 4px 0 8px; padding: 2px;`} onClick={handleSubmit}>
                    <SaveOutlinedIcon />
                  </Button>
                </InputAdornment>
              )
            }} />
            <Handicap handicap={newHandicap} onChange={(value) => setNewHandicap(value)} theme={theme} threshold={maxValue} />
          </div>
        :
        <div
          css={`
            height: inherit;
            display: flex;
            flex: 1;
            justify-content: center;
            align-items: center;
            position: relative;

            .stats {
              text-align: center;
              width: 18%;
              font-size: 16px;
              font-family: Roboto, Helvetica, Arial, sans-serif;
            }

            .max-value {
              position: relative;
            }

            cursor: ${hasMaxValue && 'pointer'};
            border: 2px solid transparent;

            &:hover {
              ${hasMaxValue && 'border: 2px solid lightgrey'};
              border-radius: 4px;
            }
          `}
          onClick={() => {
            hasMaxValue && setIsEditing(true)
          }}
        >
          <div className='stats'>{typeof defaultValue !== 'undefined' ? defaultValue : 0}</div>
          <div className='stats'>/</div>
          <div className='stats max-value'>
            {maxValue}
            {handicap > 0 &&
              <div css={`color: red; position: absolute; top: -9px; right: -9px; font-size: 12px;`}>
                -{handicap}
              </div>
            }
          </div>
        </div>
      }
    </div>
  )
}

const Info = ({ campaignData, campaignId, characters, characterId, characterName, titleClass }) => {
  const { isOpen, expander } = usePaperExpand('info')
  const theme = useTheme()

  const onSubmit = async (characterId, key, value, handicap, text) => {
    const payload = { condition: { [key]: value } }
    if (typeof handicap !== 'undefined') {
      // We check the "threshold" key name, because handicap is passed along with
      // changes for threshold
      const handicapKeyName = key === CHAR_CONDITION.threshold.wounds ? CHAR_CONDITION.handicap.wounds : CHAR_CONDITION.handicap.strain
      payload.condition[handicapKeyName] = handicap
    }
    await updateCharacter(campaignId, characterId, payload)
    if (text && text.length > 0) await addEvent(campaignId, characterName, { text })
  }

  const characterKeys = Object.keys(characters)

  const title = (
    <div className={titleClass} css={`display: flex; margin-bottom: ${!isOpen && '0'};`}>
      {expander}
      Campaign
    </div>
  )

  if (!isOpen) return title

  return (
    <>
      <div css={`display: flex; justify-content: space-between;`}>
        {title}
        <div css={`margin-bottom: 16px; display: flex; align-items: center;`}>
          {' '}
          "{campaignData.campaignName || '-'}"
          {' '}
          since
          {' '}
          {campaignData.created ?
            campaignData.created.toDate().toLocaleDateString()
            :
            '-'
          }
        </div>
      </div>
      <div css={`text-align: left;`}>
        {characterKeys.length > 0 &&
          <>
            <div css={`text-align: center; font-size: 11px; margin-bottom: 8px;`}>
              Click on wounds or strain to edit. Click anywhere else to cancel.
              <div>
                Update your wound and strain threshold in the character panel.
              </div>
            </div>
            <table css={`
              margin: 16px auto;
              border-collapse: collapse;

              thead td {
                font-weight: bold;
                text-decoration: underline;

                &.center {
                text-align: center;
              }
              }
            `}>
              <thead css={`margin-bottom: 2px;`}>
                <tr>
                  <td className='center'>Character</td>
                  <td className='center'>Wounds</td>
                  <td className='center'>Strain</td>
                </tr>
              </thead>
              <tbody>
                {characterKeys.length > 0 &&
                  characterKeys.map((charId) => {
                    const character = characters[charId]
                    const { condition, name, wounds, strain } = character.details
                    const { wounds: currWounds, strain: currStrain, woundHandicap, strainHandicap } = condition || {}

                    return (
                      <tr key={charId}>
                        <td css={`height: 40px; display: flex; align-items: center; position: relative;`}>
                          {charId === characterId && <AccountCircleIcon css={`margin-right: 4px;`} />}
                          <span css={`font-weight: ${charId === characterId && 'bold'}; line-height: 1.1; word-break: break-word;`}>
                            {name}
                          </span>
                        </td>
                        <td>
                          <EditInput
                            characterName={name}
                            handicap={woundHandicap}
                            theme={theme}
                            defaultValue={currWounds}
                            maxValue={wounds}
                            onSubmit={onSubmit.bind(this, charId, CHAR_CONDITION.threshold.wounds)}
                            type='Wounds' />
                        </td>
                        <td>
                          <EditInput
                            characterName={name}
                            handicap={strainHandicap}
                            theme={theme}
                            defaultValue={currStrain}
                            maxValue={strain}
                            onSubmit={onSubmit.bind(this, charId, CHAR_CONDITION.threshold.strain)}
                            type='Strain' />
                        </td>
                      </tr>
                    )
                  })
                }
              </tbody>
            </table>
          </>
        }
        <div css={`
          padding: 16px 16px 0 16px;
          display: flex;
          justify-content: center;
        `}>
          <div css={`
            text-align: center;
            font-size: 11px;
            padding: 12px 8px;
            border: 1px solid rgba(0,0,0,0.2);
            border-radius: 4px;
          `}>
            Copy the URL below to invite players to your campaign:
            <div css={`margin-top: 8px; display: flex;`}>
              <input id='campaignUrl' value={window.location.href} readOnly css={`
                position: absolute;
                left: -9999px;
              `} />
              <code css={`display: flex; align-items: center; word-break: break-all; padding: 8px; background-color: rgba(0, 0, 0, 0.05); border-radius: 4px;`}>
                {window.location.href}
              </code>
              <IconButton
                title='Copy to clipboard'
                css={`display: flex; align-items: center; padding: 6px; border-radius: 4px; background-color: rgba(0, 0, 0, 0.05); margin-left: 4px;`}
                onClick={() => {
                  const copyText = document.getElementById('campaignUrl')
                  if (!copyText) return
                  /* Select the text field */
                  copyText.select()
                  copyText.setSelectionRange(0, 99999) /* For mobile devices */

                  /* Copy the text inside the text field */
                  document.execCommand('copy')
                }}
              >
                <FileCopyIcon fontSize='small' css={`padding: 0;`} />
              </IconButton>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Info
